// Updated: Fixed backend URL configuration for topprop.ca deployment
import React, { useState, useEffect, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import CRMDashboard from "@/pages/CRMDashboard";
import LeadResults from "@/pages/LeadResults";
import CompanySignup from "@/pages/CompanySignup";
import ContactPage from "@/pages/ContactPage";
import PropertyManagerLogin from "@/pages/PropertyManagerLogin";
import GetMatchedPage from "@/pages/GetMatchedPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminLogin from "@/pages/AdminLogin";
import ViewMatches from "@/pages/ViewMatches";
import ViewCompanyContact from "@/pages/ViewCompanyContact";
import ViewLeadDetails from "@/pages/ViewLeadDetails";
import WhatsYourRentWorth from "@/pages/WhatsYourRentWorth";
import BlogPage from "@/pages/BlogPage";
import ReviewsPage from "@/pages/ReviewsPage";
import { Toaster } from "@/components/ui/sonner";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

function AuthHandler({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authenticated
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Set up axios interceptor to include session_token from localStorage as fallback
  // Only use localStorage token if cookie-based auth might not work (iOS Safari)
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        // Only add Authorization header if no cookie will be sent
        // This helps iOS Safari while not interfering with desktop
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken && !config.headers['Authorization'] && config.withCredentials) {
          config.headers['Authorization'] = `Bearer ${sessionToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  useEffect(() => {
    const handleAuth = async () => {
      setLoading(true);
      
      // Check if there's a session_id in the URL fragment
      const hash = location.hash;
      if (hash && hash.includes("session_id=")) {
        const sessionId = hash.split("session_id=")[1].split("&")[0];
        
        console.log("Processing session_id from OAuth callback:", sessionId);
        
        try {
          // Process session_id
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          }, { withCredentials: true });
          
          console.log("Session processed successfully:", response.data);
          setUser(response.data.user);
          
          // Clean up URL and navigate to CRM
          window.history.replaceState(null, "", "/crm");
          
          // Small delay to ensure state is updated
          setTimeout(() => {
            navigate("/crm", { replace: true });
          }, 100);
        } catch (error) {
          console.error("Authentication failed:", error);
          setUser(null);
          setLoading(false);
        }
      } else {
        // Check if user is already authenticated via session cookie
        try {
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          console.log("User already authenticated:", response.data);
          setUser(response.data);
        } catch (error) {
          // Not authenticated
          console.log("No active session");
          setUser(null);
        } finally {
          setLoading(false);
        }
      }
    };

    handleAuth();
  }, [location, navigate]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear localStorage session token
      localStorage.removeItem('session_token');
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthHandler>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/get-matched" element={<GetMatchedPage />} />
            <Route path="/results/:leadId" element={<LeadResults />} />
            <Route path="/view-matches/:leadId" element={<ViewMatches />} />
            <Route path="/view-company-contact/:leadId/:companyId" element={<ViewCompanyContact />} />
            <Route path="/view-lead-details/:matchId" element={<ViewLeadDetails />} />
            <Route path="/company-signup" element={<CompanySignup />} />
            <Route path="/property-manager-login" element={<PropertyManagerLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/whats-your-rent-worth" element={<WhatsYourRentWorth />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/crm" element={<CRMDashboard />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthHandler>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;